/**
 * Shared pdf.js page rasterization for the originator's two PDF tools: the
 * AcroForm field-mapping viewer and the e-signature placement editor.
 *
 * Both draw a different overlay on top of an identically-rendered document, and
 * the rendering is where the non-obvious behaviour lives — the CloudFront
 * SPA-fallback workaround, DPI-corrected re-rasterisation on zoom, render-task
 * cancellation, and the deliberate single-scroller layout. Two copies of that
 * would drift, and the drift would show up as a placement box that no longer
 * lines up with the page it was authored against.
 */
import type { PageViewport, PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { workflowsApi } from '@/api/workflows';

import { pdfjsLib } from './pdfFieldMapping';

/** Base rasterization scale — the 100% zoom level. */
export const SCALE = 1.3;
export const DEFAULT_ZOOM_INDEX = 1;
/**
 * Zoom steps. Dense government forms (the RDSP Application Form carries 495
 * mappable widgets) pack their boxes so tightly that the name badges overlap
 * and the boxes are too small to click accurately at 100%. Zooming re-rasters
 * the page at the new scale rather than CSS-scaling the canvas, so the PDF
 * stays sharp instead of turning to mush at the magnification you actually
 * need to read the field names.
 */
export const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 2, 2.5, 3] as const;

export type PageDim = { width: number; height: number };

export type PdfPages = {
  /** Per-page dimensions at base `SCALE`, in CSS px. Empty until parsed. */
  pages: PageDim[];
  /**
   * The parsed page proxies, for callers that need geometry beyond dimensions.
   * A ref rather than state: they are retained between the parse pass (which
   * sets `pages`) and the render pass (which draws into the canvases React has
   * only just mounted).
   */
  pageProxiesRef: React.MutableRefObject<PDFPageProxy[]>;
  loading: boolean;
  error: string | null;
};

/**
 * Fetch, parse and hold one PDF's pages.
 *
 * `onDocumentParsed` runs once per successful load, before `pages` is set, so a
 * caller can harvest whatever else it needs from the document in the same pass
 * (the field-mapping viewer takes AcroForm widget geometry there) without a
 * second parse of the same bytes.
 */
export function usePdfDocumentPages(
  templateId: string,
  fileKey: string,
  onDocumentParsed?: (pages: PDFPageProxy[]) => Promise<void> | void,
): PdfPages {
  const [pages, setPages] = useState<PageDim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageProxiesRef = useRef<PDFPageProxy[]>([]);
  const docRef = useRef<PDFDocumentProxy | null>(null);
  // Held in a ref so a caller passing an inline closure does not re-trigger the
  // parse on every render.
  const onParsedRef = useRef(onDocumentParsed);
  onParsedRef.current = onDocumentParsed;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPages([]);
    pageProxiesRef.current = [];

    (async () => {
      try {
        // Fetch the bytes ourselves rather than handing pdf.js a URL, so one request
        // drives the whole document. Goes through workflowsApi (and therefore apiClient)
        // rather than a bare fetch: a 404 comes back as 200 + index.html thanks to
        // CloudFront's SPA fallback, and only apiClient's interceptor catches that —
        // otherwise pdf.js gets handed the SPA shell and fails as a corrupt PDF.
        const data = await (await workflowsApi.downloadFile(templateId, fileKey)).arrayBuffer();
        if (cancelled) return;

        const doc = await pdfjsLib.getDocument({ data }).promise;
        if (cancelled) {
          void doc.destroy();
          return;
        }
        docRef.current = doc;

        const dims: PageDim[] = [];
        const proxies: PDFPageProxy[] = [];
        for (let p = 1; p <= doc.numPages; p++) {
          const page = await doc.getPage(p);
          if (cancelled) return;
          proxies.push(page);
          const viewport = page.getViewport({ scale: SCALE });
          dims.push({ width: viewport.width, height: viewport.height });
        }
        if (cancelled) return;

        pageProxiesRef.current = proxies;
        await onParsedRef.current?.(proxies);
        if (cancelled) return;
        setPages(dims);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (docRef.current) {
        void docRef.current.destroy();
        docRef.current = null;
      }
    };
  }, [templateId, fileKey]);

  return { pages, pageProxiesRef, loading, error };
}

/**
 * The viewport a page is currently rendered at — the single source of truth for
 * every overlay coordinate conversion.
 *
 * Callers must use THIS rather than composing `SCALE * zoom` themselves, so a
 * rotated page's flip is applied by pdf.js exactly once.
 */
export function viewportFor(page: PDFPageProxy, zoom: number): PageViewport {
  return page.getViewport({ scale: SCALE * zoom });
}

/**
 * Renders every page to a canvas in a vertical stack and lets the caller draw an
 * absolutely-positioned overlay inside each page's box.
 *
 * All pages render at once — there is no pagination. The overlay is React-
 * rendered while the canvas is imperative, so restyling a box (selection,
 * search dimming, drag state) never re-rasters the page.
 */
export function PdfPageStack({
  pages,
  zoom,
  pageProxiesRef,
  renderOverlay,
}: {
  pages: PageDim[];
  zoom: number;
  pageProxiesRef: React.MutableRefObject<PDFPageProxy[]>;
  /** Drawn inside each page's `position: relative` box, above the canvas. */
  renderOverlay: (pageIndex: number, viewport: PageViewport) => ReactNode;
}) {
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  // Bumped once the render pass has run, so overlays mount against a viewport
  // that matches what is actually on the canvas.
  const [renderedZoom, setRenderedZoom] = useState<number | null>(null);

  useEffect(() => {
    if (pages.length === 0) return;
    const tasks: RenderTask[] = [];
    const dpr = window.devicePixelRatio || 1;
    pages.forEach((dim, i) => {
      const canvas = canvasRefs.current[i];
      const page = pageProxiesRef.current[i];
      if (!canvas || !page) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // Rasterize AT the zoomed scale. `pages` holds base-scale dims, so the
      // laid-out size is scaled here too and the overlay boxes below use the
      // same multiplier — canvas and boxes stay registered at every zoom.
      const viewport = viewportFor(page, zoom);
      const cssW = dim.width * zoom;
      const cssH = dim.height * zoom;
      // Backing store scaled by DPR for crisp text; CSS size stays in layout px.
      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      const task = page.render({
        canvasContext: ctx,
        viewport,
        transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0],
      });
      tasks.push(task);
      task.promise.catch(() => {
        /* cancelled on cleanup / re-render — ignore */
      });
    });
    setRenderedZoom(zoom);
    return () => {
      for (const task of tasks) task.cancel();
    };
  }, [pages, zoom, pageProxiesRef]);

  return (
    // NO scroll container and NO fixed height here. The parent panel is already
    // `flex:1; overflow:auto`, so owning a second scroller produced two vertical
    // scrollbars stacked side by side. One scroller, in the parent.
    //
    // `minWidth: fit-content` is what makes horizontal scrolling actually work
    // at 200%+. The pages are centred, and a centred flex child that overflows
    // its container gets clipped equally on BOTH sides with no way to scroll
    // left — scrollLeft cannot go negative. Growing this wrapper to the widest
    // page means the scroll container has real width to scroll through.
    <div style={{ minWidth: 'fit-content' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
          minWidth: 'fit-content',
        }}
      >
        {pages.map((dim, i) => {
          const proxy = pageProxiesRef.current[i];
          return (
            <div
              key={i}
              style={{
                position: 'relative',
                width: dim.width * zoom,
                height: dim.height * zoom,
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                flexShrink: 0,
              }}
            >
              <canvas
                ref={(el) => {
                  canvasRefs.current[i] = el;
                }}
                style={{ display: 'block' }}
              />
              {proxy && renderedZoom === zoom && renderOverlay(i, viewportFor(proxy, zoom))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
